import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardContent,
  TextField,
  Card,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getBookings, type Commission as ApiCommission } from '../../services/commissionService';
import { settingsService } from '../../services/settingsService';


interface Commission extends ApiCommission {
  total: number;
}

const Commission: FC = () => {
  const [commissions, setCommissions] = useState<Array<ApiCommission & { total: number }>>([]);
  const [adminCommissionPercent, setAdminCommissionPercent] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filtered, setFiltered] = useState<{startDate:string; endDate:string; }>({startDate:'', endDate:''});
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0,
    totalPages: 1
  });
   
  const fetchCommissions = async () => {
      try {
        setLoading(true);
        // Get the current pagination state
        const { page, rowsPerPage } = pagination;
        
        // Fetch all data first (since the API doesn't support pagination)
        const allCommissions = await getBookings(
          undefined, // status
          filtered.startDate || undefined, // startDate
          filtered.endDate || undefined // endDate
        );
        
        const adminCommission = await settingsService.getAdminCommission();
        
        // Calculate commission totals
        const commissionsWithTotal = allCommissions.map(commission => ({
          ...commission,
          total: commission.final_amount * ((adminCommission || 0) / 100)
        }));
        
        // Client-side pagination
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedCommissions = commissionsWithTotal.slice(startIndex, endIndex);
        
        setCommissions(paginatedCommissions);
        
        // Update pagination info
        setPagination(prev => ({
          ...prev,
          total: commissionsWithTotal.length,
          totalPages: Math.ceil(commissionsWithTotal.length / rowsPerPage)
        }));
        
        setAdminCommissionPercent(adminCommission ?? 0);
        setError(null);
      } catch (err) {
        console.error('Error fetching Commissions:', err);
        setError('Failed to load Commissions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
        fetchCommissions();
      }, [filtered.startDate, filtered.endDate, pagination.page, pagination.rowsPerPage]);

      const applyDateFilter = () => {
        if (!startDate && !endDate) {
          fetchCommissions();
        }

        useEffect(() => {
          applyDateFilter();
        }, [startDate, endDate]);

        // const start = startDate ? new Date(startDate) : null;
        // const end = endDate ? new Date(endDate) : null;

        // const result = commissions.filter((item) => {
        //   const bookingDate = new Date(item.booking_date);

        //   if (start && bookingDate < start) return false;
        //   if (end && bookingDate > end) return false;

        //   return true;
        // });

        // setFiltered(result);
      };

   
  if (loading && !commissions.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  

  return (
    <div className="container-xxl flex-grow-1 ">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Typography variant="h4"  fontWeight="bold">
          Commission
        </Typography>
        
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 0, borderRadius: 2 }}>
      {/* <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <div>
          <Typography variant="body2">Start Date</Typography>
          <TextField 
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: 6 }}
          />
        </div>

        <div>
          <Typography variant="body2">End Date</Typography>
          <TextField 
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: 6 }}
          />
        </div>
        <Button variant="contained" color="primary" onClick={applyDateFilter} disabled={loading}>
            Search
          </Button>
          
        <Button 
          variant="outlined" 
          onClick={() => { setStartDate(''); setEndDate(''); }}
        >
          Reset
        </Button>
      </Box> */}
     
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Booking #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Commission %</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Commission Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commissions.length > 0 ? (
                commissions.map((booking) => (
                  <TableRow key={booking.booking_id}>
                    <TableCell>
                      <i className="bx bx-calendar-check text-primary me-3"></i>
                      <span className="fw-medium">{booking.booking_number}</span>
                    </TableCell>
                    <TableCell>
                      <div className="d-flex flex-column">
                        <span className="fw-medium">{booking.customer_name}</span>
                        <small className="text-muted">{booking.customer_mobile}</small>
                      </div>
                    </TableCell>
                     <TableCell>
                        <Chip 
                          label={booking.status}
                          color={
                            booking.status.toLowerCase() === 'completed' ? 'success' : 
                            booking.status.toLowerCase() === 'cancelled' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                    <TableCell>
                      <span className="fw-medium">{process.env.REACT_APP_SITE_CURRENCY}{booking.final_amount.toFixed(2)}</span>
                    </TableCell>
                    <TableCell>
                      {adminCommissionPercent}%
                    </TableCell>
                    <TableCell>
                      {process.env.REACT_APP_SITE_CURRENCY}{((booking.final_amount * adminCommissionPercent) / 100).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Commissions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={(event, newPage) => {
            setPagination(prev => ({ ...prev, page: newPage }));
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setPagination(prev => ({
              ...prev,
              rowsPerPage: newRowsPerPage,
              page: 0 // Reset to first page when changing rows per page
            }));
          }}
          sx={{ 
            mt: 2,
            '& .MuiTablePagination-actions': {
              '& button:disabled': {
                opacity: 0.5
              }
            }
          }}
          nextIconButtonProps={{
            disabled: pagination.page >= Math.max(0, Math.ceil(pagination.total / pagination.rowsPerPage) - 1)
          }}
          backIconButtonProps={{
            disabled: pagination.page === 0
          }}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;
          }}
        />

      </Paper>
      
    </div>
  );
}

export default Commission;
